/*
 * Rebel Athletic — Keep shoppers on the page after a Drodl add-to-cart
 * --------------------------------------------------------------------------
 * Problem (reported June 2026): on program collections, the Drodl "Smart
 * Product Options" app (afSpo) owns the Add to Bag button
 * (.product-form__cart-submit / .afSpoButton, type="button"). With the store's
 * Stiletto 2.1.1 Drodl theme config, window.af_cartType is EMPTY, so on add
 * Drodl runs `window.location.href = '/cart'` — a full-page redirect that drops
 * the shopper on the cart page, away from the collection ("launches a custom
 * cart, no easy path back to add more items").
 *
 * Drodl's own add-to-cart code branches on af_cartType:
 *     if (af_cartType === 'page')   -> window.location.href = '/cart'   (redirect)
 *     if (af_cartType === 'drawer') -> open the theme cart drawer       (stay)
 * Empty falls through to the redirect.
 *
 * v2: instead of racing to set af_cartType='drawer' before each click (a race
 * Safari lost — Drodl read the empty value first and still redirected), we LOCK
 * af_cartType as an accessor that always returns 'drawer' and ignores Drodl's
 * resets. Browser-agnostic: Drodl can read it at any time, in any order, and
 * always gets 'drawer', so it never redirects.
 *
 * Drodl's drawer-open targets stale Stiletto selectors (e.g. #cart-icon-bubble,
 * a Dawn id absent on Stiletto 5.2.2), so the drawer doesn't slide open on its
 * own. After the add we drive the THEME's own integration points: fire the
 * documented `apps:product-added-to-cart` event (Stiletto refreshes the
 * quick-cart contents + header count) and click the theme cart icon
 * ([data-js-cart-icon]) to open the drawer the same way the theme does.
 *
 * Theme-safe, mirrors rebel-attribution.js: never throws, never patches
 * fetch/XHR, no-ops when Drodl or the drawer aren't present. Fails open.
 */
(function () {
  'use strict';

  var ATC_SELECTOR = '.afSpoButton, .product-form__cart-submit';

  // 1) LOCK Drodl into "drawer" mode. An accessor that always returns 'drawer'
  //    and swallows Drodl's attempts to reset it to '' or 'page'. This removes
  //    the event-timing race the old set-before-click approach had (Safari).
  function lockDrawer() {
    try {
      var d = Object.getOwnPropertyDescriptor(window, 'af_cartType');
      if (d && typeof d.get === 'function' && d.get() === 'drawer') return; // already locked
      Object.defineProperty(window, 'af_cartType', {
        configurable: true,
        get: function () { return 'drawer'; },
        set: function () { /* ignore — keep it pinned to 'drawer' */ }
      });
    } catch (e) {
      try { window.af_cartType = 'drawer'; } catch (e2) {}
    }
  }
  lockDrawer();
  // Re-assert in case Drodl (re)defines the property after this script runs.
  document.addEventListener('DOMContentLoaded', lockDrawer);

  function cartCount() {
    try {
      return fetch('/cart.js', { credentials: 'same-origin' })
        .then(function (r) { return r.json(); })
        .then(function (c) { return (c && typeof c.item_count === 'number') ? c.item_count : null; })
        .catch(function () { return null; });
    } catch (e) { return Promise.resolve(null); }
  }

  // After Drodl adds, refresh + open the theme drawer (best-effort, fails open).
  function revealCart() {
    try { document.dispatchEvent(new CustomEvent('apps:product-added-to-cart')); } catch (e) {}
    try {
      var icon = document.querySelector('[data-js-cart-icon]');
      var hasDrawer = document.querySelector('.quick-cart');
      if (icon && hasDrawer) icon.click(); // theme preventDefaults + opens the drawer
    } catch (e) {}
  }

  // Poll until the cart grows past the pre-add count, then reveal. Bounded.
  function watchThenReveal(before) {
    var tries = 0;
    (function poll() {
      tries++;
      cartCount().then(function (now) {
        if (now != null && before != null && now > before) { revealCart(); return; }
        if (tries < 10) setTimeout(poll, 400); // ~4s ceiling
      });
    })();
  }

  // Snapshot the pre-add count as early as possible (before Drodl's add fires).
  var pendingBefore = null;
  function onPress(e) {
    try {
      var t = e.target;
      if (!t || !t.closest || !t.closest(ATC_SELECTOR)) return;
      pendingBefore = cartCount();
    } catch (err) {}
  }
  document.addEventListener('pointerdown', onPress, true);
  document.addEventListener('mousedown', onPress, true);

  // On the click itself, start watching for the add so we can open the drawer.
  document.addEventListener('click', function (e) {
    try {
      var t = e.target;
      if (!t || !t.closest || !t.closest(ATC_SELECTOR)) return;
      Promise.resolve(pendingBefore || cartCount()).then(function (before) {
        pendingBefore = null;
        watchThenReveal(before);
      });
    } catch (err) {}
  }, true);
})();
